import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts/lib/echarts';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { Tooltip, Spin, Empty } from 'antd';
import { throttle } from 'lodash';
import { useResizeObserver } from '@src/hooks';
import { calculateGraphHeight, buildGraphData } from './graphFormatUtil';
import { METRIC_CATEGORY_LIST } from './util';
import {
  GraphCompContainer,
  EchartGraphDom,
  GraphTitleContainer,
  GraphMetricCategoryContainer,
  GraphMetricCategoryTitle,
  GraphMetricCategoryImg,
  GraphMainTitle,
  GraphSubTitle,
  GraphMain,
  MetricSectionSplitter,
  MetricsContainer,
  MetricScope,
  MetricLine,
  MetricFilterLine,
  MetricSplitter,
} from './styled';
import { Link } from 'react-router-dom';
import { humanize } from '@utils';

interface MetricColumnItem {
  nodeId: string;
  filterDisplayContent: string;
  metrics: Array<MetricItem>;
}

interface MetricItem {
  metricName: string;
  value: string | number | null;
  metricField: string;
  unit: string;
  type: 'impacted' | 'non_impacted';
}

function MetricColumnList({
  nodeMetricList = [],
  nonImpactedMetrics = [],
  type,
}: {
  nodeMetricList: Array<MetricColumnItem>;
  nonImpactedMetrics: Array<MetricItem>;
  type: 'impacted' | 'non_impacted';
}) {
  const needSplitter = nodeMetricList.length > 1;
  return (
    <>
      {type === 'non_impacted'
        ? nonImpactedMetrics.map((metricItem, index) => {
            return (
              <MetricLine key={index}>
                <span>{metricItem.metricName}</span>
                <span>
                  {humanize(metricItem.value, metricItem.unit)}
                  {metricItem.unit === 'ms' ? '' : ' ' + metricItem.unit}
                </span>
              </MetricLine>
            );
          })
        : nodeMetricList.map((node, index) => {
            return (
              <div key={index}>
                {needSplitter ? (
                  <MetricFilterLine>{node.filterDisplayContent}</MetricFilterLine>
                ) : null}
                {node.metrics
                  .filter((m) => m.type === type)
                  .map((metricItem, j) => {
                    return (
                      <MetricLine key={index + '_' + j}>
                        <span>{metricItem.metricName}</span>
                        <span>
                          {humanize(metricItem.value, metricItem.unit)}
                          {metricItem.unit === 'ms' ? '' : ' ' + metricItem.unit}
                        </span>
                      </MetricLine>
                    );
                  })}
                {needSplitter && index !== nodeMetricList.length - 1 ? <MetricSplitter /> : null}
              </div>
            );
          })}
    </>
  );
}

export default function ServicesGraph({ diagnosticData, loading, alertId }) {
  const graphContainer = useRef<HTMLDivElement>(null);
  const echartsInstanceRef = React.useRef<echarts.ECharts>();
  const [containerHeight, setContainerHeight] = useState(0);

  const graphData = diagnosticData.graph || [];
  const metricContexts = diagnosticData.metricScopes || {};

  const setDiagnosticInLocalStorage = () => {
    const alertItem = {
      session: metricContexts?.session?.trendsParam,
      event: metricContexts?.event?.trendsParam,
      span: metricContexts?.span?.trendsParam,
    };
    const localStorageKey = `eco-server-alert-${alertId}`;
    localStorage.setItem(localStorageKey, JSON.stringify(alertItem || ''));
  };

  useEffect(() => {
    if (!graphContainer.current || graphData.length === 0) {
      return;
    }
    const height = calculateGraphHeight(graphData);
    setContainerHeight(height);
  }, [graphData]);

  useEffect(() => {
    if (!graphContainer.current) {
      return;
    }
    if (containerHeight) {
      const option = buildGraphData(graphContainer.current.clientWidth, graphData);
      echartsInstanceRef.current =
        echartsInstanceRef.current || echarts.init(graphContainer.current);
      echartsInstanceRef.current?.setOption(option);
      echartsInstanceRef.current.on('click', (params) => {
        if (!params?.data?.customObj?.context) {
          // if users click the lines just return and do nothing
          return;
        }
        setDiagnosticInLocalStorage();
        setTimeout(() => {
          window.open(
            `${window.conviva.baseUrl}/dashboard/trends/${params.data.customObj.context}/${alertId}`,
            '_blank',
          );
        }, 300);
      });
    }
  }, [containerHeight]);

  const onResize = React.useMemo(
    () =>
      throttle(() => {
        if (!graphContainer.current || graphData.length === 0) {
          return;
        }
        const bounding = graphContainer.current?.getBoundingClientRect();

        // Check the bounding box is not empty, otherwise echarts will throw error
        if (!((bounding?.width || 0) * (bounding?.height || 0))) return;

        const option = buildGraphData(graphContainer.current.clientWidth, graphData);
        echartsInstanceRef.current?.setOption(option);
        echartsInstanceRef.current?.resize({ animation: { duration: 200 } });
      }, 200),
    [graphData],
  );

  // Resize chart automatically on the container resize
  useResizeObserver({
    ref: graphContainer,
    onResize: () => requestAnimationFrame(onResize),
  });

  return (
    <GraphMain className="graph">
      <GraphMainTitle>Diagnosis Diagram & Evidence</GraphMainTitle>
      <GraphSubTitle>
        The Experience Impact’s relationship to the system architecture performance statistics
      </GraphSubTitle>
      <GraphCompContainer>
        {/* graph echart dom */}
        <EchartGraphDom
          style={{
            height: containerHeight + 'px',
          }}
          ref={graphContainer}
        ></EchartGraphDom>
        {loading ? <Spin style={{ display: 'block', height: '100px', marginTop: '60px' }} /> : null}
        {!loading && graphData.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: '60px 0 20px' }} />
        ) : null}
        {/* title part */}
        <GraphTitleContainer style={{ height: containerHeight + 'px' }}>
          {METRIC_CATEGORY_LIST.map((category, index) => {
            return (
              <GraphMetricCategoryContainer key={index}>
                <Link
                  onClick={setDiagnosticInLocalStorage}
                  to={`${window.conviva.baseUrl}/dashboard/trends/${category.context}/${alertId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    position: 'relative',
                    zIndex: 5,
                    textDecoration: 'none',
                  }}
                >
                  <div>
                    <GraphMetricCategoryImg src={category.icon} />
                    <GraphMetricCategoryTitle>{category.title}</GraphMetricCategoryTitle>
                  </div>
                </Link>
              </GraphMetricCategoryContainer>
            );
          })}
        </GraphTitleContainer>
        {/* metric part impacted metrics */}
        <MetricsContainer>
          <MetricScope>
            <MetricColumnList
              nodeMetricList={metricContexts?.session?.nodeMetricList || []}
              nonImpactedMetrics={metricContexts?.session?.nonImpactedMetrics || []}
              type="impacted"
            />
          </MetricScope>
          <MetricScope>
            <MetricColumnList
              nodeMetricList={metricContexts?.event?.nodeMetricList || []}
              nonImpactedMetrics={metricContexts?.event?.nonImpactedMetrics || []}
              type="impacted"
            />
          </MetricScope>
          <MetricScope>
            <MetricColumnList
              nodeMetricList={metricContexts?.span?.nodeMetricList || []}
              nonImpactedMetrics={metricContexts?.span?.nonImpactedMetrics || []}
              type="impacted"
            />
          </MetricScope>
        </MetricsContainer>
        <MetricSectionSplitter>
          Metrics for Non-Impacted Cohort
          <Tooltip title="The dimensions and metrics of the users not affected">
            <QuestionCircleOutlined style={{ marginLeft: '4px', cursor: 'pointer' }} />
          </Tooltip>
        </MetricSectionSplitter>
        {/* metric part non-impacted metrics */}
        <MetricsContainer>
          <MetricScope>
            <MetricColumnList
              nodeMetricList={metricContexts?.session?.nodeMetricList || []}
              nonImpactedMetrics={metricContexts?.session?.nonImpactedMetrics || []}
              type="non_impacted"
            />
          </MetricScope>
          <MetricScope>
            <MetricColumnList
              nodeMetricList={metricContexts?.event?.nodeMetricList || []}
              nonImpactedMetrics={metricContexts?.event?.nonImpactedMetrics || []}
              type="non_impacted"
            />
          </MetricScope>
          <MetricScope>
            <MetricColumnList
              nodeMetricList={metricContexts?.span?.nodeMetricList || []}
              nonImpactedMetrics={metricContexts?.span?.nonImpactedMetrics || []}
              type="non_impacted"
            />
          </MetricScope>
        </MetricsContainer>
      </GraphCompContainer>
    </GraphMain>
  );
}
